import type { TargetingRule, UrlCondition } from '@/state/surveys/survey-targeting';
import { Plus, X } from 'lucide-react';
import { cn } from 'helpers/cn';
import { URL_TARGETING_CONDITIONS } from 'definitions';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { invalidPattern } from '@/state/surveys/survey-targeting';
import { FOCUS_RING } from '../../ConsolePanel';

type PageRulesProps = {
  rules: TargetingRule[];
  canAdd: boolean;
  onChange: (rules: TargetingRule[]) => void;
};

// Same field look as the experiment's URL targeting, so a rule reads the same on both screens.
const FIELD = 'border-line bg-field text-foreground h-9 rounded-lg text-body';

/*
 * The runtime compares a rule with the page's full address (helpers/url-match), so
 * a bare path only works where the condition looks inside the address. Each example
 * is one that would actually match.
 */
const EXAMPLE: Record<UrlCondition, string> = {
  equals: 'https://example.com/checkout',
  'not equals': 'https://example.com/checkout',
  'starts with': 'https://example.com/blog',
  contains: '/checkout',
  'does not contain': '/account',
  'ends with': '/thank-you',
  'matches regex': '/products/\\d+',
};

const ADD_BUTTON = 'text-dim hover:text-foreground hover:bg-hover h-8 rounded-lg px-2';

/**
 * The survey's page rules, one row each: condition, value, remove.
 *
 * The condition is a Radix Select without a name. useForm syncs named fields
 * through the DOM, and its change handler ignores nameless ones, so the hidden
 * native select Radix renders inside a form stays out of the form state; the rules
 * are controlled through updateDraft like every other list in the wizard.
 */
export const PageRules = ({ rules, canAdd, onChange }: PageRulesProps) => {
  const setRule = (index: number, change: Partial<TargetingRule>) =>
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...change } : rule)));

  const addRule = () => onChange([...rules, { url: '', condition: 'contains', device: null }]);

  if (rules.length === 0) {
    return (
      <div className="border-line rounded-card flex items-center justify-between gap-3 border border-dashed py-1.5 pr-1.5 pl-3">
        <span className="supporting">Every page on the website.</span>
        {canAdd && (
          <Button type="button" variant="ghost" onClick={addRule} className={ADD_BUTTON}>
            <Plus className="size-4" />
            Add page rule
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <ul className="bg-panel2 border-line rounded-card divide-line m-0 list-none divide-y border p-0">
        {rules.map((rule, index) => {
          const condition = (rule.condition ?? 'contains') as UrlCondition;
          const invalid = invalidPattern(rule);
          return (
            <li key={index} className="flex flex-wrap items-center gap-2 p-2 sm:flex-nowrap">
              {/* Rules are alternatives, so every row after the first says so. */}
              <span className="micro-label w-8 shrink-0 text-center">{index === 0 ? 'URL' : 'or'}</span>
              <Select value={condition} onValueChange={(value) => setRule(index, { condition: value as UrlCondition })}>
                <SelectTrigger aria-label={`Page rule ${index + 1} condition`} className={cn(FIELD, 'w-40 shrink-0')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-line bg-panel rounded-lg">
                  {URL_TARGETING_CONDITIONS.map((option) => (
                    <SelectItem key={option} value={option} className="text-body">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* On a phone the value takes its own full-width line, below the condition and remove. */}
              <Input
                value={rule.url ?? ''}
                onChange={(ev) => setRule(index, { url: ev.target.value })}
                placeholder={EXAMPLE[condition]}
                maxLength={2048}
                spellCheck={false}
                aria-label={`Page rule ${index + 1} value`}
                aria-invalid={invalid}
                className={cn(
                  FIELD,
                  'order-4 min-w-0 basis-full sm:order-none sm:flex-1 sm:basis-auto',
                  condition === 'matches regex' && 'font-mono',
                  invalid && 'border-danger',
                )}
              />
              <button
                type="button"
                onClick={() => onChange(rules.filter((_, i) => i !== index))}
                aria-label={`Remove page rule ${index + 1}`}
                className={cn(
                  'text-dim hover:text-foreground hover:bg-hover order-3 ml-auto grid size-9 shrink-0 place-items-center rounded-lg transition-colors duration-150 sm:order-none sm:ml-0',
                  FOCUS_RING,
                )}
              >
                <X className="size-4" />
              </button>
            </li>
          );
        })}
      </ul>
      {canAdd && (
        <Button type="button" variant="ghost" onClick={addRule} className={ADD_BUTTON}>
          <Plus className="size-4" />
          Add page rule
        </Button>
      )}
      <p className="supporting m-0">
        A page qualifies when it matches any one rule. Rules are checked against the full address, query string
        included, and a trailing slash is ignored.
      </p>
    </div>
  );
};
